import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { BarChart } from '../../../../../components/muiLegacy/Charts/BarChart';
import ChartLegend from '../../../../../components/muiLegacy/Charts/ChartLegend';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import { LegendIntervalsGq } from '../../../IntervalsAndColorsLogic';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import NotEnoughTagGroups from '../../NotEnoughTagGroups';
import { getBarValues, segmentNameInterval, wordCountIntervals } from './HBLtChartByTopic.logic';

export interface HBLtChartByTopicProps extends ChartProps { }

const HBLtChartByTopic: React.FC<HBLtChartByTopicProps> = ({
  project,
  setAnalysisFilterState,
  analysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showSerpOnly: true, showTagGroupIds: true, showHBLt: true },
      })),
    []
  );

  const segmentNames = wordCountIntervals(analysisFilterState, language).map(
    (x) =>
      x.name +
      ' (' +
      segmentNameInterval(x.startCount, x.endCount) +
      (x.endCount === 1
        ? ` ${internationalText.chart.keyword_specificity_by_topic.word[language]})`
        : ` ${internationalText.chart.keyword_specificity_by_topic.words[language]})`)
  );

  // Fetch data
  const { result, query } = projectsApiService.queries.analysis.useGetHBLtPerTagGroup({
    projectId: project.id,
    tagGroupIds: analysisFilterState?.tagGroupIds,
    wordCountIntervals: wordCountIntervals(analysisFilterState, language),
    serpFilter: analysisFilterState?.serpOnly,
  });

  const loading = query.loading;
  const tagGroupAnalyses = result?.tagGroupAnalyses;

  // Transform data
  const bars = useMemo(
    () =>
      getBarValues(
        wordCountIntervals(analysisFilterState, language),
        tagGroupAnalyses ?? [],
        language
      ),
    [tagGroupAnalyses]
  );

  const legend: LegendIntervalsGq[] = [
    { name: segmentNames[0], bgcolor: palette.primaryScale[0] },
    { name: segmentNames[1], bgcolor: palette.primaryScale[1] },
    { name: segmentNames[2], bgcolor: palette.primaryScale[2] },


  ];

  // Render
  if (didQueryTimeout(query)) {
    return <DataTimedOut />;
  }

  if (loading) {
    return <AnalysisChartLoading />;
  }

  if (!analysisFilterState?.tagGroupIds?.length) {
    return <NotEnoughTagGroups />;
  }

  if (!tagGroupAnalyses?.some((tg) => tg.hBLtAnalyses?.some((a) => a.searchVolume > 0))) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <ChartLegend intervals={legend} />
      <BarChart
        palette={palette}
        colors="standard"
        leftLegend={internationalText.chart.keyword_specificity_by_topic.legend[language]}
        ResponsiveBarProps={{
          data: bars,
          keys: segmentNames,
          indexBy: 'barName',
        }}
      />
    </div>
  );
};

export default HBLtChartByTopic;
